<!--
  The annotation workspace: canvas on the left, guided palette and controls on the right.

  This component owns the keyboard, because the shortcuts *are* the workflow. It resolves an
  event through `lib/keymap.ts` (never inline key comparisons) and dispatches to the session.
  Events originating in a text field are ignored, so typing a note does not toggle draw mode
  halfway through a sentence.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import { api } from '../lib/api/client';
  import { asProblem, type ApiProblem } from '../lib/api/problem';
  import { readingFlow } from '../lib/draft';
  import { isOpenWork, sharingNotice } from '../lib/imageStatus';
  import {
    blurOnPointerActivation,
    isInteractiveTarget,
    isTypingTarget,
    matchAction,
    type Action,
  } from '../lib/keymap';
  import { href, router } from '../lib/router.svelte';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { recordNav } from '../lib/stores/nav.svelte';
  import { queue } from '../lib/stores/queue.svelte';
  import { session } from '../lib/stores/session.svelte';
  import { advancesByQueueItem } from '../lib/taskViews';
  import BoxInspector from './BoxInspector.svelte';
  import ImageCanvas from './ImageCanvas.svelte';
  import InscriptionAttributes from './InscriptionAttributes.svelte';
  import ProgressMeter from './ProgressMeter.svelte';
  import QueueReasons from './QueueReasons.svelte';
  import ResetRecord from './ResetRecord.svelte';
  import RecordNavSidebar from './RecordNavSidebar.svelte';
  import { playLetter } from '../lib/audio';
  import ListenControl from './ListenControl.svelte';
  import RecordPager from './RecordPager.svelte';
  import SaveStatus from './SaveStatus.svelte';
  import ShortcutHelp from './ShortcutHelp.svelte';
  import TokenStrip from './TokenStrip.svelte';

  interface Props {
    imageId: number;
  }

  const { imageId }: Props = $props();

  let helpOpen = $state(false);
  let fitToken = $state(0);
  let zoomToken = $state(0);
  let zoomDirection = $state(1);
  let navProblem = $state<ApiProblem | null>(null);
  let confirmIncomplete = $state(false);
  let confirmNotPictured = $state(false);
  let classPickerToken = $state(0);
  let canvas = $state<ImageCanvas | null>(null);

  // `untrack` so the effect depends on the `imageId` prop and on nothing else. `open()` reads
  // and writes a dozen session fields; tracked, every one of them would become a dependency
  // and the load would restart itself.
  $effect(() => {
    const id = imageId;
    untrack(() => void session.open(id));
  });

  // Leaving the page with unsaved boxes should at least warn. The autosave loop and the
  // localStorage draft are the real protection; this is the last line.
  $effect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (session.saveState === 'dirty' || session.saveState === 'retrying' || session.saveState === 'failed') {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  });

  function dispatch(action: Action): boolean {
    switch (action) {
      case 'draw-mode':
        session.drawMode = !session.drawMode;
        // Turning draw mode *on* without an identity is the reported failure: the label flips,
        // the capability does not, and nothing says why. Ask at the moment of the request.
        if (session.drawMode && !annotator.ready) annotator.requestIdentity('draw');
        return true;
      case 'play-letter': {
        // The chip under the cursor, not the selected box: the cursor is what the letter
        // shortcuts already act on, and a box may not exist yet.
        const token = session.inscription?.tokens?.[session.cursor];
        if (token) void playLetter(token.letter_class);
        return true;
      }
      case 'next-letter':
        session.stepCursorBy(1);
        return true;
      case 'prev-letter':
        session.stepCursorBy(-1);
        return true;
      case 'toggle-uncertain':
        if (session.selected) session.toggleFlag(session.selected.key, 'uncertain');
        return true;
      case 'toggle-restored':
        if (session.selected) session.toggleFlag(session.selected.key, 'restored');
        return true;
      case 'cycle-chirality':
        if (session.selected) session.cycleChirality(session.selected.key);
        return true;
      case 'delete-box':
        if (session.selected) session.deleteBox(session.selected.key);
        return true;
      case 'mark-absent':
        // Via the session, so the "cursor is past the last chip" case says something instead of
        // looking like a dead key.
        session.markCurrentAbsent();
        return true;
      case 'insert-unexpected':
        session.armInsert();
        return true;
      case 'relabel-selected':
        // The only keyboard path to a class change, and it always reports what it did. A chip
        // click deliberately does not do this — see `TokenStrip.svelte`.
        session.relabelSelectedToCursor();
        return true;
      case 'class-picker':
        if (session.selected) classPickerToken += 1;
        return true;
      case 'select-next-box':
        session.selectAdjacent(1);
        return true;
      case 'select-prev-box':
        session.selectAdjacent(-1);
        return true;
      case 'deselect':
        // The help text has always said Esc cancels the drag in progress; `cancelDrag` was
        // exported and never called, so it did not. It does now.
        canvas?.cancelDrag();
        canvas?.cancelStraighten();
        session.select(null);
        confirmIncomplete = false;
        confirmNotPictured = false;
        helpOpen = false;
        return true;
      case 'complete-advance':
        void completeAndAdvance();
        return true;
      case 'skip-image':
        void skipAndAdvance();
        return true;
      case 'not-pictured':
        void notPicturedAndAdvance();
        return true;
      case 'prev-record':
        // Positional, and guarded: `recordNav.go` flushes the session before it moves. It walks
        // the same array the sidebar renders, so the two can never disagree about "next".
        void recordNav.go(-1);
        return true;
      case 'next-record':
        void recordNav.go(1);
        return true;
      case 'toggle-record-nav':
        recordNav.toggleSidebar();
        return true;
      case 'save-now':
        void session.retryNow();
        return true;
      case 'undo':
        session.undo();
        return true;
      case 'redo':
        session.redo();
        return true;
      // Punctuation turns the photograph; letters turn the selected box. Both go through the
      // canvas, which owns the viewport — the workspace never computes a transform.
      case 'rotate-view-ccw':
        canvas?.rotateBy(-1);
        return true;
      case 'rotate-view-cw':
        canvas?.rotateBy(1);
        return true;
      case 'rotate-view-ccw-coarse':
        canvas?.rotateBy(-15);
        return true;
      case 'rotate-view-cw-coarse':
        canvas?.rotateBy(15);
        return true;
      case 'rotate-view-reset':
        canvas?.resetRotation();
        return true;
      case 'straighten':
        canvas?.armStraighten();
        return true;
      case 'rotate-box-ccw':
        if (session.selected) session.rotateBox(session.selected.key, -1);
        return true;
      case 'rotate-box-cw':
        if (session.selected) session.rotateBox(session.selected.key, 1);
        return true;
      case 'rotate-box-ccw-coarse':
        if (session.selected) session.rotateBox(session.selected.key, -15);
        return true;
      case 'rotate-box-cw-coarse':
        if (session.selected) session.rotateBox(session.selected.key, 15);
        return true;
      case 'rotate-box-reset':
        canvas?.squareSelectedBox();
        return true;
      case 'zoom-in':
        zoomDirection = 1;
        zoomToken += 1;
        return true;
      case 'zoom-out':
        zoomDirection = -1;
        zoomToken += 1;
        return true;
      case 'zoom-fit':
        fitToken += 1;
        return true;
      case 'help':
        helpOpen = !helpOpen;
        return true;
      default:
        return false;
    }
  }

  /**
   * The workspace owns the keyboard — but not while a control has focus.
   *
   * This handler used to `preventDefault()` everything it matched, and `isTypingTarget` only
   * excludes text entry. So with focus on a button or a link, `Enter` completed the image
   * instead of activating the control, `Space` stepped the letter cursor and `Tab` moved the
   * box selection: the workspace was not keyboard-navigable at all while it was mounted.
   *
   * Now the browser wins whenever focus is on something focusable. That costs nothing on the
   * fast path because pointer activation blurs the control again
   * (`blurOnPointerActivation`), so clicking a chip or a toolbar button hands the keyboard
   * straight back.
   *
   * `Esc` is the one exception, and it has to be. Standing down for it too meant a
   * keyboard-only annotator who tabbed into the toolbar had no in-page way back to the canvas
   * shortcuts at all: every key was handed to the focused control, including the key whose
   * whole job is "put me back". So `Esc` blurs the active control first, then runs as
   * `deselect`.
   */
  $effect(() => {
    const handler = (event: KeyboardEvent) => {
      // The identity dialog this used to yield to is gone: identity comes from the sign-in
      // now, so there is no longer a modal that can appear over the canvas. `isTypingTarget`
      // still covers the case that actually matters — keys going to a focused field.
      if (isTypingTarget(event.target)) return;
      const action = matchAction(event, readingFlow(session.readingDirection));
      if (action === 'deselect') {
        const active = document.activeElement as { blur?: () => void } | null;
        if (isInteractiveTarget(active) && typeof active?.blur === 'function') active.blur();
      } else if (
        isInteractiveTarget(event.target) ||
        isInteractiveTarget(document.activeElement)
      ) {
        return;
      }
      if (!action) return;
      if (dispatch(action)) event.preventDefault();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // --- navigation ------------------------------------------------------------------

  async function nextImageId(): Promise<number | null> {
    const inscription = session.inscription;
    if (inscription) {
      // An open sibling photograph of this record comes first, whatever task was last chosen:
      // `queue.taskId` outlives the queue page, so a record opened from the browser after a
      // second pass still has its own unfinished photographs, and jumping past them would be a
      // surprise. A finished second-pass record has none, so the branch below still applies.
      const sibling = inscription.images.find(
        (image) => image.id !== imageId && isOpenWork(image),
      );
      if (sibling) return sibling.id;
      // A task whose work is on photographs the boxing has already finished (`second-pass`) has
      // no "open" image in the next record either — every photograph of a finished record is
      // done or set aside, so the search below would come back empty and dump the annotator on
      // the queue page. Take the next record in the queue and the image the server chose for it.
      if (advancesByQueueItem(queue.taskId)) {
        return queue.nextImageAfter(inscription.id);
      }
      const next = queue.nextAfter(inscription.id);
      if (next) {
        try {
          const detail = await api.inscriptionById(next.id);
          const candidate = detail.images.find(isOpenWork);
          if (candidate) return candidate.id;
        } catch (error) {
          navProblem = asProblem(error);
        }
      }
    }
    return null;
  }

  /**
   * Complete-and-advance's move: the next thing to *do*.
   *
   * It shares its ordering with the sidebar and the pager — `queue.nextAfter` walks the same
   * `queue.items` array through `lib/nav.ts` — and differs only in the predicate: this one skips
   * images and records that are already finished, because the annotator asked for the next piece
   * of work rather than the next row. `[` / `]` are positional, so they can step back onto a
   * finished record to fix a box.
   */
  async function advance(): Promise<void> {
    const next = await nextImageId();
    if (next !== null) router.go({ name: 'image', imageId: next });
    // Back to the task being worked, not to the chooser: the annotator picked a job and
    // finishing its last image is not a reason to ask them to pick again.
    else router.go({ name: 'queue', task: queue.taskId });
    void queue.load();
  }

  async function completeAndAdvance(): Promise<void> {
    if (session.unresolvedTokens.length > 0 && !confirmIncomplete) {
      confirmIncomplete = true;
      return;
    }
    confirmIncomplete = false;
    if (await session.markComplete()) await advance();
  }

  async function skipAndAdvance(): Promise<void> {
    if (await session.skip()) await advance();
  }

  /**
   * Confirmed once, because it is easy to be wrong and the mistake is quiet.
   *
   * Getting this wrong on a panel photograph means declaring that a text is not in a frame it
   * is in, and the photograph then leaves every queue with nothing saying why anybody should
   * look again. Unlike a skip — which the next annotator re-encounters — this one settles the
   * question, so it asks first. Any boxes already drawn stay; `session.markNotPictured` does
   * not touch them.
   */
  async function notPicturedAndAdvance(): Promise<void> {
    if (!confirmNotPictured) {
      confirmNotPictured = true;
      return;
    }
    confirmNotPictured = false;
    if (await session.markNotPictured()) await advance();
  }

  /**
   * The active task's headline number, for the meter beside the per-image one.
   *
   * The first meter is the primary one by the API's contract ("primary meter first"), so this
   * takes it rather than naming a key — a task whose headline is images rather than letters
   * should not need a change here.
   */
  const taskMeter = $derived(queue.progress?.meters[0] ?? null);

  /** Null when nothing shares this photograph, so the notice is absent rather than reassuring. */
  const panelNotice = $derived(sharingNotice(session.image?.shared_with_sigla ?? []));
</script>

<div class="workspace" style="--nav-col: {recordNav.sidebarOpen ? '264px' : '44px'}">
  <RecordNavSidebar />

  <div class="canvas-col">
    {#if session.loading}
      <div class="panel">Loading image {imageId}…</div>
    {:else if session.loadProblem}
      <div class="banner error">
        <strong>Could not open this image</strong>
        <p class="small">{session.loadProblem.message}</p>
        <p class="small muted mono">code: {session.loadProblem.code}</p>
        <a href={href({ name: 'queue', task: queue.taskId })}>Back to the work queue</a>
      </div>
    {:else}
      <ImageCanvas bind:this={canvas} {fitToken} {zoomToken} {zoomDirection} />
      <div class="row wrap toolbar small">
        <button
          class:primary={session.drawMode}
          onclick={(event) => {
            session.drawMode = !session.drawMode;
            blurOnPointerActivation(event);
          }}
          disabled={!session.canDraw}
        >
          {session.drawMode ? 'Draw mode on' : 'Draw mode off'} <kbd>D</kbd>
        </button>
        <button
          class:primary={session.insertMode}
          onclick={(event) => {
            session.armInsert();
            blurOnPointerActivation(event);
          }}
          disabled={session.readOnly}
          title="The next box is a letter the transliteration does not contain"
        >
          Insert unexpected <kbd>I</kbd>
        </button>
        <button
          onclick={(event) => {
            session.markCurrentAbsent();
            blurOnPointerActivation(event);
          }}
          disabled={session.readOnly}
          title="Record that the current expected letter is not present on the stone"
        >
          Not present <kbd>N</kbd>
        </button>
        <button
          onclick={(event) => {
            session.relabelSelectedToCursor();
            blurOnPointerActivation(event);
          }}
          disabled={session.readOnly || !session.selected}
          title="Move the selected box onto the chip under the cursor. Clicking a chip only moves the cursor; a class change is never implicit."
        >
          Relabel to cursor <kbd>L</kbd>
        </button>
        <span class="spacer"></span>
        <button
          class="subtle"
          onclick={(event) => {
            dispatch('zoom-out');
            blurOnPointerActivation(event);
          }}
          aria-label="Zoom out">−</button
        >
        <button
          class="subtle"
          onclick={(event) => {
            dispatch('zoom-in');
            blurOnPointerActivation(event);
          }}
          aria-label="Zoom in">+</button
        >
        <button
          class="subtle"
          onclick={(event) => {
            dispatch('zoom-fit');
            blurOnPointerActivation(event);
          }}>Fit <kbd>0</kbd></button
        >
        <button
          class="subtle"
          onclick={(event) => {
            session.undo();
            blurOnPointerActivation(event);
          }}
          disabled={!session.canUndo}
        >
          Undo
        </button>
        <button
          class="subtle"
          onclick={(event) => {
            session.redo();
            blurOnPointerActivation(event);
          }}
          disabled={!session.canRedo}
        >
          Redo
        </button>
        <button
          class="subtle"
          onclick={(event) => {
            helpOpen = true;
            // Blur, or focus stays on the button and `?` / Esc cannot close the sheet again.
            blurOnPointerActivation(event);
          }}>Shortcuts <kbd>?</kbd></button
        >
      </div>
    {/if}
  </div>

  <aside class="side">
    <div class="panel col">
      <div class="row wrap">
        <h1>{session.inscription?.siglum_raw ?? session.image?.siglum ?? `image ${imageId}`}</h1>
        <span class="spacer"></span>
        <!-- Beside the name of what it resets. Its confirmation takes the full width of this
             row, so it opens under the heading rather than beside it. -->
        <ResetRecord />
        <a class="small" href={href({ name: 'queue', task: queue.taskId })}>Queue</a>
      </div>

      <!-- The pager, on the same controller as the sidebar list. Rendered here too so the
           position and the step controls stay reachable with the sidebar collapsed and the
           annotator's eye already on the record header. -->
      <RecordPager variant="bar" />

      <!-- What the task being worked wants done here, when it says (the second pass does). -->
      <QueueReasons inscriptionId={session.inscription?.id ?? null} />

      <!--
        Before the work, not after it. This photograph is one OCIANA filed under several
        inscriptions on the same rock face, so the frame carries texts that are not this
        record's and may not carry this record's at all. Discovering that after hunting for
        letters that were never there is the wasted half-hour this line exists to prevent.

        A statement, not a warning: the photograph is perfectly good and the row is correctly
        linked. It is the premise underneath — that an image filed under a record depicts that
        record — that does not hold on this corpus.
      -->
      {#if panelNotice}
        <p class="small muted" role="note">
          {panelNotice}. It may not show this text at all — if it does not, use
          <strong>Not this inscription</strong> (<kbd>Shift+X</kbd>) rather than skipping.
        </p>
      {/if}
      {#if recordNav.refusal || recordNav.problem}
        <div class="banner warn small col" role="status">
          {#if recordNav.refusal}<p>{recordNav.refusal}</p>{/if}
          {#if recordNav.problem}
            <p>Could not open the next record: {recordNav.problem.message}</p>
            <p class="muted mono">code: {recordNav.problem.code}</p>
          {/if}
          <div class="row">
            <button class="small" onclick={() => void session.retryNow()}>Save now</button>
            <button class="small subtle" onclick={() => recordNav.dismissRefusal()}>Dismiss</button>
          </div>
        </div>
      {/if}

      <!--
        The transliteration stays out in the open: it is the work. What sat around it — the S3
        key, the English translation, the OCIANA link — is provenance you consult occasionally
        and read past constantly, and in a 420px column that is three lines of the rail spent
        on none of the task.
      -->
      {#if session.inscription?.translit}
        <div class="glyph translit">{session.inscription.translit}</div>
        <!-- Under the line it reads, not in a panel of its own: what it offers is a reading OF
             this transliteration, and its own caveats travel with it. -->
        {#if session.inscription.tokens?.length}
          <ListenControl
            tokens={session.inscription.tokens}
            siglum={session.inscription.siglum}
          />
        {/if}
      {/if}
      {#if session.image?.storage_key || session.inscription?.translation || session.inscription?.ociana_url}
        <details class="small record-details">
          <summary class="muted">Record details</summary>
          {#if session.inscription?.translation}
            <p class="muted">{session.inscription.translation}</p>
          {/if}
          {#if session.image?.storage_key}
            <p class="muted mono wrap-any">{session.image.storage_key}</p>
          {/if}
          {#if session.inscription?.ociana_url}
            <a href={session.inscription.ociana_url} target="_blank" rel="noreferrer">
              OCIANA record
            </a>
          {/if}
        </details>
      {/if}
      {#if session.image?.link !== 'linked' && session.image}
        <p class="banner warn small">
          This image is <strong>{session.image.link}</strong>: it is not attributed to a single
          record, so there is no expected-letter sequence for it.
        </p>
      {/if}

      <!--
        The inscription's own attributes, at the foot of the record header rather than in a
        panel of their own further down.

        They belong to the thing this panel names. Reading direction, layout and the stone's
        other photographs are all facts about the *record* in the `<h1>` above — putting them
        below the box inspector, which is about one rectangle, had them reading as properties of
        the image being edited, which is exactly the confusion that moved the columns onto
        `inscriptions` in the first place.
      -->
      <InscriptionAttributes {imageId} />

      <SaveStatus />
    </div>

    <div class="panel col">
      <TokenStrip />
      <ProgressMeter
        label="This image"
        value={session.progress.resolved}
        total={session.progress.expected_letters}
        tone={session.progress.complete ? 'ok' : 'accent'}
        detail={`${session.progress.boxed_expected} boxed · ${session.progress.absent} not present · ${session.progress.unexpected} unexpected`}
      />
      <!--
        The task, not the corpus. This meter used to read "All images" over every photograph
        in the database — a denominator no amount of work in this session could move, which is
        a demoralising thing to sit next to for an hour. It now counts the job the annotator
        chose, over the scope that job is defined on.
      -->
      {#if taskMeter}
        <ProgressMeter
          label={`${queue.task?.label ?? 'This task'} · ${queue.task?.scope_label ?? ''}`}
          value={taskMeter.value}
          total={taskMeter.total}
          detail={taskMeter.detail}
          tone={taskMeter.total > 0 && taskMeter.value >= taskMeter.total ? 'ok' : 'accent'}
        />
      {/if}
    </div>

    <BoxInspector focusToken={classPickerToken} />

    <!--
      Pinned, not last.

      `Complete & next` closes the loop and was below the fold in a scrolling column — on a
      1280×800 laptop you had to scroll the rail to finish an image, every image, and the
      keyboard path (`Enter`) was the only one that did not. Sticky rather than moved to the
      top: it is still the end of the panel order, it just cannot leave the screen.
    -->
    <div class="panel col finish">
      {#if confirmIncomplete}
        <div class="banner warn small">
          <strong>{session.unresolvedTokens.length} expected letter(s) are unresolved.</strong>
          <p>
            An unresolved letter is one that is neither boxed nor marked not-present. Mark them
            not present if the stone does not show them; press complete again to finish anyway.
          </p>
        </div>
      {/if}
      <div class="row wrap">
        <button
          class="primary"
          onclick={(event) => {
            blurOnPointerActivation(event);
            void completeAndAdvance();
          }}
          disabled={session.readOnly || !annotator.ready}
        >
          Complete &amp; next <kbd>Enter</kbd>
        </button>
        <button
          onclick={(event) => {
            blurOnPointerActivation(event);
            void skipAndAdvance();
          }}
          disabled={session.readOnly || !annotator.ready}
        >
          Skip <kbd>Shift+S</kbd>
        </button>
        <button
          class:danger={confirmNotPictured}
          onclick={(event) => {
            blurOnPointerActivation(event);
            void notPicturedAndAdvance();
          }}
          disabled={session.readOnly || !annotator.ready}
        >
          {confirmNotPictured ? 'Confirm: not this inscription' : 'Not this inscription'}
          <kbd>Shift+X</kbd>
        </button>
      </div>
      {#if confirmNotPictured}
        <p class="small">
          This records that the photograph does not show <strong>{session.image?.siglum ?? 'this record'}</strong>
          — a different statement from skipping it, and one that takes it out of every queue.
          Boxes already drawn here are kept. Press again to confirm, or <kbd>Esc</kbd> to
          cancel.
        </p>
      {/if}
      {#if !annotator.ready}
        <div class="row wrap small">
          <span class="muted">Nothing can be saved until this session has an annotator id.</span>
          <button class="primary small" onclick={() => annotator.requestIdentity('edit')}>
            Set annotator id
          </button>
        </div>
      {/if}
      {#if navProblem}
        <p class="small muted">Could not look ahead in the queue: {navProblem.message}</p>
      {/if}
    </div>
  </aside>
</div>

<ShortcutHelp open={helpOpen} onclose={() => (helpOpen = false)} />

<style>
  .workspace {
    display: grid;
    /*
      Record list · canvas · guided palette. `--nav-col` is set inline from the persisted
      collapse state so the canvas reclaims the width rather than the rail overlaying it.

      The palette was a flat `420px`. Added to the 264px record list that is open by default,
      that is 684px of chrome — on a 1280px laptop, **53% of the width**, leaving the
      photograph about 17% of the screen. The photograph is the thing being read. So the rail
      is now proportional with a floor the chips still fit in and the old width as its ceiling:
      420px on a wide monitor, ~333px at 1280px, never below 320px.
    */
    grid-template-columns:
      var(--nav-col, 264px)
      minmax(0, 1fr)
      clamp(320px, 26vw, 420px);
    gap: 12px;
    height: 100%;
    min-height: 0;
    padding: 12px;
    /* `min-width: 0` on the grid itself: without it the fixed and clamped tracks can sum past
       the viewport and the *page* scrolls sideways, which is what it did at 1280px. */
    min-width: 0;
    /*
      No transition on `grid-template-columns` — removed with the canvas refit, not for taste.

      A 140ms ease used to run here. It is the wrong thing to animate now that `ImageCanvas`
      re-fits on container resize: the eased track fires the `ResizeObserver` on every
      intermediate frame, and each one re-fits the Konva stage. Collapsing the rail went from
      one refit to roughly a dozen, with the photograph rescaling through all of them. An
      instant snap is both cheaper and steadier to look at.
    */
  }

  /* The `prefers-reduced-motion` override that used to be here is gone with the transition it
     disabled: there is no motion left on this grid to reduce. */

  .canvas-col {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    min-height: 0;
  }

  .toolbar {
    flex: none;
    /* Ten controls at the default 5px/10px padding needed ~930px and wrapped to a second row
       below that, and the row it took came out of the canvas — the one part of this screen
       whose size is the point. Tightened here rather than by dropping labels or `<kbd>` hints:
       the shortcuts printed on the buttons are how the keyboard workflow is discovered. */
    gap: 6px;
  }

  .toolbar :global(button) {
    padding: 4px 7px;
  }

  /* Seven of the ten buttons carry a shortcut chip; 2px a side across them is the difference
     between one row and two here. The chips stay — they are the only place the keyboard
     workflow announces itself without opening the sheet. */
  .toolbar :global(kbd) {
    padding: 1px 3px;
  }

  .side {
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    padding-right: 4px;
  }

  /* Never scrolls out of reach. `--bg` behind it so the panels above scroll under it rather
     than showing through. */
  .finish {
    position: sticky;
    bottom: 0;
    background: var(--bg-panel);
    box-shadow: 0 -8px 12px -8px var(--bg);
  }

  .record-details > p,
  .record-details > a {
    margin: 4px 0 0;
    display: block;
  }

  /* An S3 key has no spaces to break at, so it would otherwise widen the whole rail. */
  .wrap-any {
    overflow-wrap: anywhere;
  }

  /* Default `display: list-item` is kept deliberately — the disclosure marker is the only
     thing saying this opens, and `display: flex` on a summary drops it in Chrome. Only the
     "Record details" disclosure lives here now; the inscription panel took its own styles
     with it into `InscriptionAttributes.svelte`. */
  summary {
    cursor: pointer;
  }

  .translit {
    font-size: 1.3rem;
    word-break: break-word;
  }

  @media (max-width: 1100px) {
    .workspace {
      grid-template-columns: 1fr;
      /* Record list, then canvas, then palette. The list keeps its own scroll but is capped so
         it cannot push the canvas off the screen on a narrow window. */
      grid-template-rows: auto minmax(320px, 55vh) auto;
      height: auto;
    }

    /* The collapsed rail has no single-column form; `RecordNavSidebar` ignores the collapse
       preference at this width rather than growing a second layout. `--nav-col` stops
       mattering here because the grid is one column. */

    /* Sticky has nothing to stick to once the page scrolls as one column. */
    .finish {
      position: static;
      box-shadow: none;
    }
  }
</style>
